import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminApi, ApiError } from '../lib/api';
import type { AdminBundleDetail, AdminBundleItem, BundleKind, ScriptSummary } from '../../shared/types';
import { reorder } from '../lib/reorder';
import Wordmark from '../components/Wordmark';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/Button';
import Eyebrow from '../components/Eyebrow';
import ItemsEditor, { type EditorItem } from '../components/BundleItemsEditor';
import BundleLinkPanel from '../components/BundleLinkPanel';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  title: string;
  clientName: string;
  clientEmail: string;
  kind: BundleKind;
  introNote: string;
  items: EditorItem[];
}

const BLANK_FORM: FormState = {
  title: '',
  clientName: '',
  clientEmail: '',
  kind: 'review',
  introNote: '',
  items: [],
};

function detailToForm(detail: AdminBundleDetail): FormState {
  return {
    title: detail.title,
    clientName: detail.client_name ?? '',
    clientEmail: detail.client_email ?? '',
    kind: detail.kind,
    introNote: detail.intro_note ?? '',
    items: detail.items.map(itemToEditorItem),
  };
}

function itemToEditorItem(item: AdminBundleItem): EditorItem {
  return {
    key: item.id,
    script_id: item.script_id,
    placeholder_title: item.placeholder_title,
    show_before_ready: item.show_before_ready,
    scriptInfo: item.script,
    warning: item.warning,
  };
}

/** What actually gets saved — drops the display-only fields (scriptInfo,
 *  warning) and the React key, so the dirty check can't false-positive on
 *  server-resolved metadata the user never touched. */
function snapshot(form: FormState): string {
  return JSON.stringify({
    title: form.title.trim(),
    client_name: form.clientName.trim(),
    client_email: form.clientEmail.trim(),
    kind: form.kind,
    intro_note: form.introNote.trim(),
    items: form.items.map((it) => ({
      script_id: it.script_id,
      placeholder_title: it.placeholder_title,
      show_before_ready: it.show_before_ready,
    })),
  });
}

function newKey(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Bundle ("Link") editor — dedicated page shared by create (/admin/links/new)
 * and edit (/admin/links/:id). See docs/script-bundles-plan.md §6 and
 * mockups/script-bundles.html §02. Item-list rendering + the add-script/
 * add-placeholder popovers live in BundleItemsEditor; the right-hand link
 * panel (rotate/send/archive/delete) lives in BundleLinkPanel.
 */
export default function AdminBundleEditor() {
  const { id: routeId } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [loading, setLoading] = useState(routeId !== undefined);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Link-panel fields — only meaningful once the bundle has been saved at
  // least once (bundleId !== null). Kept separate from `form` because they
  // aren't part of the editable payload.
  const [bundleId, setBundleId] = useState<string | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [archivedAt, setArchivedAt] = useState<number | null>(null);
  const [readyEmailedAt, setReadyEmailedAt] = useState<number | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const initialSnapshotRef = useRef(snapshot(BLANK_FORM));
  // Tracks which bundle id the form currently reflects, so the load effect
  // below can skip re-fetching right after a create-then-navigate — we
  // already have the freshest state in memory at that point.
  const loadedForRef = useRef<string | null | undefined>(undefined);

  const [activeScripts, setActiveScripts] = useState<ScriptSummary[] | null>(null);

  useEffect(() => {
    adminApi.listScripts({ view: 'active' }).then((res) => setActiveScripts(res.scripts));
  }, []);

  useEffect(() => {
    if (loadedForRef.current === (routeId ?? null)) return;
    loadedForRef.current = routeId ?? null;

    if (!routeId) {
      setForm(BLANK_FORM);
      setBundleId(null);
      setSlug(null);
      setArchivedAt(null);
      setReadyEmailedAt(null);
      initialSnapshotRef.current = snapshot(BLANK_FORM);
      setLoading(false);
      return;
    }
    setLoading(true);
    adminApi
      .getBundle(routeId)
      .then((detail) => {
        const next = detailToForm(detail);
        setForm(next);
        setBundleId(detail.id);
        setSlug(detail.slug);
        setArchivedAt(detail.archived_at);
        setReadyEmailedAt(detail.ready_emailed_at);
        initialSnapshotRef.current = snapshot(next);
        setLoading(false);
      })
      .catch((err) => {
        setLoadError(err instanceof ApiError ? err.message : 'Failed to load this link.');
        setLoading(false);
      });
  }, [routeId]);

  const dirty = snapshot(form) !== initialSnapshotRef.current;

  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  function handleBackClick(e: React.MouseEvent) {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) {
      e.preventDefault();
    }
  }

  function patch(fields: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...fields }));
  }

  function setItems(updater: (items: EditorItem[]) => EditorItem[]) {
    setForm((prev) => ({ ...prev, items: updater(prev.items) }));
  }

  async function handleSave() {
    const title = form.title.trim();
    if (!title) {
      setSaveError('Title is required.');
      return;
    }
    const clientEmail = form.clientEmail.trim();
    if (clientEmail && !EMAIL_RE.test(clientEmail)) {
      setSaveError('Enter a valid client email, or leave it blank.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    const payload = {
      title,
      client_name: form.clientName.trim() || null,
      client_email: clientEmail || null,
      kind: form.kind,
      intro_note: form.introNote.trim() || null,
      items: form.items.map((it) => ({
        script_id: it.script_id,
        placeholder_title: it.placeholder_title,
        show_before_ready: it.show_before_ready,
      })),
    };
    try {
      if (!bundleId) {
        const result = await adminApi.createBundle(payload);
        setBundleId(result.id);
        setSlug(result.slug);
        setArchivedAt(null);
        setReadyEmailedAt(null);
        initialSnapshotRef.current = snapshot(form);
        loadedForRef.current = result.id;
        navigate(`/admin/links/${result.id}`, { replace: true });
      } else {
        const detail = await adminApi.updateBundle(bundleId, payload);
        const next = detailToForm(detail);
        setForm(next);
        setSlug(detail.slug);
        setArchivedAt(detail.archived_at);
        setReadyEmailedAt(detail.ready_emailed_at);
        initialSnapshotRef.current = snapshot(next);
      }
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function addScript(script: ScriptSummary) {
    setItems((items) => [
      ...items,
      {
        key: newKey(),
        script_id: script.id,
        placeholder_title: null,
        show_before_ready: true,
        scriptInfo: { id: script.id, slug: script.slug, title: script.title, status: script.status },
        warning: null,
      },
    ]);
  }

  function addPlaceholder(title: string) {
    setItems((items) => [
      ...items,
      {
        key: newKey(),
        script_id: null,
        placeholder_title: title,
        show_before_ready: true,
        scriptInfo: null,
        warning: null,
      },
    ]);
  }

  function removeItem(key: string) {
    setItems((items) => items.filter((it) => it.key !== key));
  }

  function toggleReady(key: string, showBeforeReady: boolean) {
    setItems((items) =>
      items.map((it) => (it.key === key ? { ...it, show_before_ready: showBeforeReady } : it)),
    );
  }

  function moveItem(index: number, delta: number) {
    setItems((items) => reorder(items, index, index + delta));
  }

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  function handleDrop(index: number) {
    setItems((items) => (dragIndex === null ? items : reorder(items, dragIndex, index)));
    setDragIndex(null);
  }

  // client_name lookups for item meta lines — enriches items resolved from
  // GET/PATCH (which only carry id/slug/title/status) using the active
  // scripts list already fetched for the picker. Items whose script has
  // since been archived/trashed won't be in `active` — that's fine, the
  // API's warning string already explains those.
  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of activeScripts ?? []) map.set(s.id, s.client_name);
    return map;
  }, [activeScripts]);

  const excludedScriptIds = useMemo(
    () => new Set(form.items.map((it) => it.script_id).filter((v): v is string => v !== null)),
    [form.items],
  );

  if (loadError) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center">
        <p className="text-accent">{loadError}</p>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center">
        <p className="text-faint font-mono text-[12px] uppercase tracking-[0.12em]">Loading…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-page text-fg">
      <header className="border-b border-line bg-page/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 min-h-16 py-2 md:py-0 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <Link
              to="/admin"
              onClick={handleBackClick}
              className="flex items-center gap-3 text-muted hover:text-fg transition-colors"
            >
              <span aria-hidden>←</span>
              <Wordmark size="sm" />
            </Link>
            <ThemeToggle />
            <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.12em] text-faint border-l border-line pl-3 ml-1">
              {bundleId ? 'Editing link' : 'New link'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
              <span
                className={`w-1.5 h-1.5 rounded-full ${dirty ? 'bg-accent animate-pulse' : 'bg-faint/40'}`}
                aria-hidden
              />
              {dirty ? 'Unsaved changes' : 'Saved'}
            </span>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </header>

      {saveError && (
        <div className="bg-accent/10 border-b border-accent/40 text-accent text-[13px] px-6 py-3 text-center">
          {saveError}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 md:px-8 py-8 md:py-12 grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6 md:gap-8">
        <div className="min-w-0 space-y-6">
          <div>
            <FieldLabel>Title</FieldLabel>
            <TextInput
              value={form.title}
              onChange={(v) => patch({ title: v })}
              placeholder="e.g. Spring Campaign — Scripts"
              maxLength={300}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel>Client name</FieldLabel>
              <TextInput
                value={form.clientName}
                onChange={(v) => patch({ clientName: v })}
                placeholder="Client name"
                maxLength={300}
              />
            </div>
            <div>
              <FieldLabel>Client email</FieldLabel>
              <TextInput
                value={form.clientEmail}
                onChange={(v) => patch({ clientEmail: v })}
                placeholder="hello@client.com"
                type="email"
              />
            </div>
          </div>

          <div>
            <FieldLabel>Kind</FieldLabel>
            <KindToggle value={form.kind} onChange={(kind) => patch({ kind })} />
          </div>

          <div>
            <FieldLabel>
              Intro note{' '}
              <span className="normal-case tracking-normal text-faint font-normal">
                (shows on the hub, from you)
              </span>
            </FieldLabel>
            <textarea
              value={form.introNote}
              onChange={(e) => patch({ introNote: e.target.value })}
              rows={3}
              maxLength={5000}
              placeholder="A line or two to welcome them in…"
              className="
                w-full px-4 py-3 bg-elevated border border-line rounded-sm
                text-fg text-[14px] placeholder:text-faint leading-relaxed
                focus:border-accent focus:shadow-focus focus:outline-none
                transition-all duration-150 resize-y
              "
            />
          </div>

          <ItemsEditor
            items={form.items}
            activeScripts={activeScripts}
            excludedScriptIds={excludedScriptIds}
            clientNameById={clientNameById}
            dragIndex={dragIndex}
            onDragStart={setDragIndex}
            onDrop={handleDrop}
            onDragEnd={() => setDragIndex(null)}
            onMove={moveItem}
            onAddScript={addScript}
            onAddPlaceholder={addPlaceholder}
            onRemove={removeItem}
            onToggleReady={toggleReady}
          />
        </div>

        <div className="min-w-0">
          {bundleId && slug ? (
            <BundleLinkPanel
              bundleId={bundleId}
              slug={slug}
              kind={form.kind}
              archivedAt={archivedAt}
              readyEmailedAt={readyEmailedAt}
              dirty={dirty}
              onSlugRotated={setSlug}
              onArchivedChanged={setArchivedAt}
              onReadyEmailSent={setReadyEmailedAt}
              onDeleted={() => navigate('/admin', { replace: true })}
            />
          ) : (
            <div className="bg-card border border-line rounded-md p-5">
              <Eyebrow>// Bundle link</Eyebrow>
              <p className="text-muted text-[13px] leading-relaxed mt-3">
                Save this link to get a shareable URL, plus rotate, send the
                ready email, archive, or delete it.
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

// ---------- Small field primitives ----------

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
      {children}
    </span>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  maxLength?: number;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="
        w-full px-4 py-3 bg-elevated border border-line rounded-sm
        text-fg text-[15px] placeholder:text-faint
        focus:border-accent focus:shadow-focus focus:outline-none
        transition-all duration-150
      "
    />
  );
}

function KindToggle({ value, onChange }: { value: BundleKind; onChange: (v: BundleKind) => void }) {
  return (
    <div className="inline-flex rounded-full bg-elevated border border-line p-0.5">
      {(
        [
          { id: 'review', label: 'Client review' },
          { id: 'shoot', label: 'Shoot-day pack' },
        ] as Array<{ id: BundleKind; label: string }>
      ).map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-3.5 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] font-medium transition-colors duration-200 ${
            value === opt.id ? 'bg-fg text-page' : 'text-muted hover:text-fg'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
